'use client';

// P29.4 / D119: marketing-surface locale switcher. Swaps the leading /it or
// /en segment via router.replace, preserving the rest of the pathname (anchor
// + query). Fires a Plausible custom event `Locale Toggle` when present
// (P29.20 wires the real script; this component is forward-compatible).

import { useRouter, usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { trackEvent } from '@/lib/analytics/plausible';

type SupportedLocale = 'it' | 'en';

export function LanguageToggle() {
  const router = useRouter();
  const pathname = usePathname() ?? '/';
  const currentLocale = useLocale() as SupportedLocale | string as SupportedLocale;
  const t = useTranslations('commonShared.localeNames');
  const tHeader = useTranslations('marketing.header');

  function switchTo(target: SupportedLocale) {
    if (target === currentLocale) return;
    const next = pathname.replace(/^\/(it|en)(?=\/|$)/, `/${target}`);
    trackEvent('Locale Toggle', { from: currentLocale, to: target });
    router.replace(next);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-toggle-trigger"
        className="rounded-md border border-slate-200 px-2 py-1 text-xs"
        aria-label={tHeader('languageToggle')}
      >
        {currentLocale === 'it' ? '🇮🇹' : '🇬🇧'} {currentLocale.toUpperCase()}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="language-toggle-it" onClick={() => switchTo('it')}>
          🇮🇹 {t('it')}
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="language-toggle-en" onClick={() => switchTo('en')}>
          🇬🇧 {t('en')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
